import React from 'react';
import { motion } from 'motion/react';
import { Play, Settings2, Share2, Music, Box, Activity } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import SEO from '@/components/SEO';

const HowItWorks = () => {
  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <SEO 
        title="How It Works | Studio" 
        description="Learn the simple steps to creating stunning, rhythmic physics simulations in our advanced studio."
      />

      {/* Ambient background effects */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[120px] translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="container relative z-10 mx-auto px-4 pt-32 pb-20">
        <div className="text-center max-w-3xl mx-auto mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tighter mb-6 bg-clip-text text-transparent bg-linear-to-b from-foreground to-foreground/50">
              Create the ultimate ball engine experience.
            </h1>
            <p className="text-xl text-muted-foreground leading-relaxed">
              Our platform is the most advanced ball bouncing simulator on the web. 
              Follow these simple steps to bring your rhythmic vision to life in our high-performance ball engine.
            </p>
          </motion.div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {/* Step 1 */}
          <StepCard 
            number="01"
            title="Pick Your Ball Engine Mode"
            description="Choose from Rhythmic, Spawner, or Escape. Each mode changes how the ball physics simulations behave and how they respond to your creative input."
            icon={<Box className="w-6 h-6 text-primary" />}
            index={0}
          />

          {/* Step 2 */}
          <StepCard 
            number="02"
            title="Sync the Beat"
            description="Integrate audio to drive your ball bouncing simulator. Our engine automatically syncs the ball physics to the rhythm of your favorite tracks."
            icon={<Music className="w-6 h-6 text-primary" />}
            index={1}
          />

          {/* Step 3 */}
          <StepCard 
            number="03"
            title="Fine-Tune Ball Physics"
            description="Tweak the ball physics simulations with precision. Adjust bounce, weight, and speed to create the most satisfying visual experience."
            icon={<Settings2 className="w-6 h-6 text-primary" />}
            index={2}
          />

          {/* Step 4 */}
          <StepCard 
            number="04"
            title="Run on Any Browser"
            description="Our ball engine on browser technology ensures smooth, lag-free simulations without any downloads. Watch thousands of balls interact in real-time."
            icon={<Activity className="w-6 h-6 text-primary" />}
            index={3}
          />

          {/* Step 5 */}
          <StepCard 
            number="05"
            title="Render & Export"
            description="Once satisfied, use our Cloud Renderer to export high-definition video files. Our specialized server-side rendering ensures every frame is crisp and perfectly synced with the master clock."
            icon={<Play className="w-6 h-6 text-primary" />}
            index={4}
          />

          {/* Step 6 */}
          <StepCard 
            number="06"
            title="Share with the World"
            description="Export your masterpiece and share it directly on TikTok, Instagram, or YouTube. Our simulations are designed to go viral—showing off your unique aesthetic and rhythmic timing to your followers."
            icon={<Share2 className="w-6 h-6 text-primary" />}
            index={5}
          />
        </div>

        <motion.div 
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="mt-32 p-12 rounded-[2rem] bg-foreground/[0.02] border border-foreground/[0.05] text-center"
        >
          <h2 className="text-3xl font-bold mb-6">Ready to start creating?</h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            The studio is open. No complex software installations required. Everything runs directly in your browser with professional precision.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="rounded-full px-8 h-14 text-base font-bold bg-primary hover:scale-105 transition-transform">
              <Link to="/studio">Open Studio Now</Link>
            </Button>
            <Button variant="outline" size="lg" className="rounded-full px-8 h-14 text-base font-bold hover:bg-foreground hover:text-background transition-all">
              <Link to="/help">Read Documentation</Link>
            </Button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

const StepCard = ({ number, title, description, icon, index }: { number: string; title: string; description: string; icon: React.ReactNode; index: number }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1, duration: 0.5 }}
      className="group p-8 rounded-3xl bg-foreground/[0.01] border border-foreground/[0.03] hover:bg-foreground/[0.02] hover:border-foreground/[0.08] transition-all duration-300"
    >
      <div className="flex justify-between items-start mb-6">
        <div className="w-12 h-12 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:scale-110 transition-transform">
          {icon}
        </div>
        <span className="text-4xl font-black text-foreground/5">{number}</span>
      </div>
      <h3 className="text-xl font-bold mb-4">{title}</h3>
      <p className="text-muted-foreground text-sm leading-relaxed">
        {description}
      </p>
    </motion.div>
  );
};

export default HowItWorks;
